import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2 } from 'lucide-react';
import { VideoLessonsTableProps } from '../types';

export function VideoLessonsTable({
  isDark,
  videoLessons,
  isLoading,
  onEdit,
  onDelete,
  formatDate,
}: VideoLessonsTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow
          className={cn(
            isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'
          )}
        >
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Sort
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Title
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Type
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            URL
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Updated
          </TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Actions
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {videoLessons.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-12 text-slate-500">
              No video lessons found
            </TableCell>
          </TableRow>
        ) : (
          videoLessons.map((lesson) => (
            <TableRow
              key={lesson.id}
              className={cn(
                'transition-colors',
                isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'
              )}
            >
              <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                {lesson.sort_id}
              </TableCell>
              <TableCell
                className={cn('font-medium', isDark ? 'text-white' : 'text-[#0F172A]')}
              >
                {lesson.title}
              </TableCell>
              <TableCell className={cn('uppercase text-xs', isDark ? 'text-slate-300' : 'text-slate-600')}>
                {lesson.type}
              </TableCell>
              <TableCell className={cn('max-w-[260px] truncate', isDark ? 'text-slate-300' : 'text-slate-600')}>
                {lesson.url}
              </TableCell>
              <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>
                {formatDate(lesson.updated_at)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(lesson)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100'
                    )}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(lesson.id)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark
                        ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-red-600'
                    )}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
